// src/composables/useToast.js

import { ref } from "vue";

const toasts = ref([]);

let lastId = 0;

const SUCCESS_TIMEOUT = 4000;
const ERROR_TIMEOUT = 8000;

const statusTitles = {
    400: "Bad request",
    401: "Not signed in",
    403: "Not allowed",
    404: "Not found",
    405: "Not allowed",
    409: "Conflict",
    413: "File too large",
    419: "Session expired",
    422: "Invalid data",
    429: "Too many requests",
};

const statusMessages = {
    400: "The server could not understand the request.",
    401: "Your session has expired. Please sign in again.",
    403: "You do not have permission to do this.",
    404: "The requested item was not found.",
    405: "This action is not available.",
    409: "This item conflicts with existing data.",
    413: "The uploaded file is too large.",
    419: "Your session has expired. Please refresh the page and try again.",
    422: "Please check the highlighted fields and try again.",
    429: "Too many requests. Please wait a moment and try again.",
};

export const dismissToast = (id) => {
    toasts.value = toasts.value.filter(toast => toast.id !== id);
};

export const showToast = ({
    variant = "primary",
    title,
    message = "",
    details = [],
    status = null,
    timeout = SUCCESS_TIMEOUT,
}) => {
    const id = ++lastId;

    toasts.value.push({ id, variant, title, message, details, status });

    if (timeout) {
        setTimeout(() => dismissToast(id), timeout);
    }

    return id;
};

export const notifySuccess = ({ title, message = "", status = null }) =>
    showToast({ variant: "success", title, message, status, timeout: SUCCESS_TIMEOUT });

// Laravel returns { message, errors: { field: [message] } } on 422.
const validationDetails = (data) => {
    const fieldErrors = data?.errors;

    if (!fieldErrors || typeof fieldErrors !== "object") {
        return [];
    }

    return Object.values(fieldErrors)
        .reduce((all, messages) => all.concat(messages), [])
        .slice(0, 5);
};

export const notifyRequestError = (error, fallbackTitle = "Request failed") => {
    if (error && typeof error === "object") {
        error.toastShown = true;
    }

    const response = error?.response;

    if (!response) {
        return showToast({
            variant: "danger",
            title: "Connection failed",
            message: error?.message || "The server could not be reached.",
            timeout: ERROR_TIMEOUT,
        });
    }

    const status = response.status;
    const data = response.data || {};

    const backendMessage = typeof data.message === "string" ? data.message.trim() : "";

    const message = backendMessage
        || statusMessages[status]
        || (status >= 500 ? "Something went wrong on the server." : "The request could not be completed.");

    const title = statusTitles[status]
        || (status >= 500 ? "Server error" : fallbackTitle);

    return showToast({
        variant: "danger",
        title,
        message,
        details: status === 422 ? validationDetails(data) : [],
        status,
        timeout: ERROR_TIMEOUT,
    });
};

export default function useToast() {
    return {
        toasts,
        showToast,
        dismissToast,
        notifySuccess,
        notifyRequestError,
    };
}
